// The pure rules for a count metric, kept in one module so they can be tested without
// React or a store — the same arrangement `Utils/crossOriginCacheBust.mjs` uses.
//
// Every one of these decides what the backend receives or what a coach reads, and each
// has a wrong answer that looks plausible. They are the parts worth pinning.

/** `count-flags` -> `flags`. Null for any measured metric id. */
export function countSourceFromMetricId(metricId) {
  if (typeof metricId !== "string" || !metricId.startsWith("count-")) return null;
  const source = metricId.slice("count-".length);
  return ["flags", "statuses", "injuries", "treatments"].includes(source) ? source : null;
}

/**
 * The identifier type a query carries.
 *
 * Derived from the metric id rather than trusted from the configurator: the backend
 * rejects a `count-…` key carrying type "metric", and a configurator that stores the id
 * but forgets the type would emit exactly that mismatch.
 */
export function identifierType(metricId, stored) {
  // Uses the same validated rule as `countSourceFromMetricId`, not a bare `count-`
  // prefix. A lookalike id such as "count-foo" names no source the backend has, so it
  // must not be labelled a count — that would emit a count identifier with no spec,
  // which the backend rejects.
  if (countSourceFromMetricId(metricId) !== null) return "count";
  return stored ?? "metric";
}

/**
 * The `count` object a query carries, or undefined for a measured metric.
 *
 * `reading` is injuries-only: the backend refuses it on any other source, so it is
 * omitted rather than defaulted.
 */
export function countFields(spec) {
  if (!spec) return undefined;
  return {
    source: spec.source,
    include: spec.include ?? [],
    match: spec.match ?? "all",
    ...(spec.source === "injuries" ? { reading: spec.reading ?? "incidence" } : {}),
  };
}

/** The spec a freshly chosen source starts from. */
export function defaultCountSpec(source) {
  return source === "injuries"
    ? { source, include: [], match: "all", reading: "incidence" }
    : { source, include: [], match: "all" };
}

/**
 * What a count appends to its metric name: the first checked item, how many more, and
 * the match mode when it is not the default.
 *
 * Only `any` is marked. `all` is the default, and two widgets differing only in mode
 * must not read the same.
 */
export function countSuffix(spec) {
  const include = spec?.include ?? [];
  if (include.length === 0) return "";
  const first = include[0].split(":").slice(1).join(":") || include[0];
  const more = include.length > 1 ? ` +${include.length - 1}` : "";
  const mode = spec?.match === "any" && include.length > 1 ? " (any)" : "";
  return `: ${first}${more}${mode}`;
}

/**
 * The `<metricId>-<agg>` key a query carries.
 *
 * A count always totals: the backend accepts only `sum` and rejects `count-flags-avg`.
 * Derived here rather than trusted from the configurator, for the same reason as
 * `identifierType` — the gauge needle falls back to "avg" when its agg is unset, which
 * would have emitted exactly that rejected key.
 */
export function metricKeyFor(metricId, agg) {
  if (countSourceFromMetricId(metricId) !== null) return `${metricId}-sum`;
  return `${metricId}-${agg}`;
}

/** Whether a count of this source honours the exclude-injured toggle. */
export function honoursInjuredToggle(source) {
  return source === "flags" || source === "statuses";
}
